'use client';

// "Use in my room" on every row of a gear list (/gear/c/<category>): the step
// from browsing gear to building with it.
//
// A guest, or anyone without a saved room, gets a new project with the
// product already in it (lib/gear/productSeed.ts: its place in a system, or
// the room's display). Someone signed in with rooms is asked which: one of
// theirs, which opens with the product's card up so they say what it does
// there (lib/gear/roomPick.ts) — nothing in a saved room changes without them
// — or a new room with it in.
//
// The rooms are read once for the whole list (RoomChoicesProvider), never per
// row: a category lists a hundred products.

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { createContext, useContext, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import ActionMenu from '@/components/ui/ActionMenu';
import Button, { buttonClass } from '@/components/ui/Button';
import { EVENTS, track } from '@/lib/analytics';
import { roomWithProductHref } from '@/lib/gear/roomPick';
import { useSavedRooms, type SavedRoom } from '@/lib/session/savedRooms';
import { useSessionSummary } from '@/lib/session/sessionSummary';

const RoomChoices = createContext<SavedRoom[] | null>(null);

/** The signed-in visitor's rooms, for every row under it. */
export function RoomChoicesProvider({ children }: { children: ReactNode }) {
  const { signedIn } = useSessionSummary();
  return <RoomChoices.Provider value={useSavedRooms(signedIn)}>{children}</RoomChoices.Provider>;
}

/** The rooms a menu offers, newest first; the rest are one click from /rooms. */
const MAX_ROOMS = 6;

const LABEL = 'Use in my room';

export default function UseInRoom({ productId, name, newRoomHref }: {
  productId: string;
  /** The product's name, so each row's control says which product it is. */
  name: string;
  /** A new project with the product in it. */
  newRoomHref: string;
}) {
  const rooms = useContext(RoomChoices);
  const router = useRouter();
  // The funnel's own event, as every way from a marketing page into the
  // editor sends it; a saved room's token stays out of analytics.
  const record = (room: 'new' | 'saved') => track(EVENTS.landing_cta_clicked, {
    location: 'gear_row',
    destination: room === 'new' ? newRoomHref : '/room/[token]',
    product_id: productId,
    room,
  });

  if (!rooms || rooms.length === 0) {
    return (
      <Link
        href={newRoomHref}
        // A hundred rows must not prefetch a hundred editor pages.
        prefetch={false}
        onClick={() => record('new')}
        aria-label={`${LABEL}: ${name}`}
        className={buttonClass({ variant: 'secondary', size: 'sm', className: 'whitespace-nowrap' })}
        data-testid="use-in-room"
      >
        {LABEL}
      </Link>
    );
  }

  return (
    <ActionMenu
      label={`${LABEL}: ${name}`}
      title="Which room?"
      testId="use-in-room"
      items={[
        ...rooms.slice(0, MAX_ROOMS).map((r) => ({
          key: r.token,
          label: r.title,
          onSelect: () => { record('saved'); router.push(roomWithProductHref(r.token, productId)); },
        })),
        { key: 'new', label: 'A new room', onSelect: () => { record('new'); router.push(newRoomHref); } },
      ]}
      trigger={(props) => (
        <Button
          {...props}
          variant="secondary"
          size="sm"
          aria-label={`${LABEL}: ${name}`}
          className="whitespace-nowrap"
          data-testid="use-in-room"
        >
          {LABEL}
          <ChevronDown className="h-3.5 w-3.5" aria-hidden />
        </Button>
      )}
    />
  );
}
